import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  listValidationProfiles,
  createValidationProfile,
  updateValidationProfile,
  deleteValidationProfile,
  duplicateValidationProfile,
  activateValidationProfile,
  deactivateValidationProfile,
  runValidationProfile,
  runAllValidationProfiles,
} from "../lib/adminApi";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  RefreshCw, CheckCircle2, AlertTriangle, XCircle, MinusCircle, Search,
  Play, Pencil, Copy, Trash2, Eye, Power, PowerOff, Plus, ShieldCheck, TrendingUp,
} from "lucide-react";

// The current APPROVED benchmark (informational only — no snapshots/history).
const KNOWN_WARNINGS = ["GF-08", "GF-09", "CM-06"];
const APPROVED_BENCHMARK = { total: 30, pass: 27, warning: 3, fail: 0, accuracy: "30/30" };

const STATUS_META = {
  PASS: { label: "PASS", color: "#047857", bg: "#ECFDF5", border: "#047857", Icon: CheckCircle2 },
  WARNING: { label: "WARNING", color: "#B45309", bg: "#FFFBEB", border: "#B45309", Icon: AlertTriangle },
  FAIL: { label: "FAIL", color: "#B91C1C", bg: "#FEF2F2", border: "#B91C1C", Icon: XCircle },
  NOT_RUN: { label: "NOT RUN", color: "#6B7280", bg: "#F3F4F6", border: "#9CA3AF", Icon: MinusCircle },
  NOT_CONFIGURED: { label: "NOT RUN", color: "#6B7280", bg: "#F3F4F6", border: "#9CA3AF", Icon: MinusCircle },
};

const TYPE_LABELS = {
  greenfield: "Greenfield",
  brownfield: "Brownfield",
  cloud_migration: "Cloud Migration",
};

const EMPTY_FORM = {
  profile_code: "",
  profile_name: "",
  assessment_type: "greenfield",
  description: "",
  business_context: "",
  active: true,
  display_order: 0,
  module_data_json: "{}",
  expected_solution: "",
  expected_deployment: "",
  expected_hosting: "",
  expected_complexity: "",
  expected_confidence: "",
  expected_migration_strategy: "",
  expected_reason: "",
};

function statusOf(p) {
  return (p.latest_result && p.latest_result.status) || "NOT_RUN";
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.NOT_RUN;
  const { Icon } = m;
  return (
    <span
      data-testid={`vp-status-${status}`}
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5 border"
      style={{ color: m.color, background: m.bg, borderColor: m.border + "55" }}
    >
      <Icon className="w-3 h-3" />
      {m.label}
    </span>
  );
}

function ExpectedActual({ expected, actual, isDiff }) {
  const norm = (s) => (s || "").trim().toLowerCase().replace("sap ", "");
  const mismatch = isDiff !== undefined ? isDiff : (expected && norm(expected) !== norm(actual));
  return (
    <div className="leading-tight">
      <div className="text-[12px] text-[#0F172A]">{actual || "—"}</div>
      {expected && (
        <div className={`text-[10px] ${mismatch ? "text-[#B91C1C]" : "text-[#6B7280]"}`}>
          exp: {expected}
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, color, testid }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <span className="text-2xl font-bold leading-none" style={{ color, fontFamily: "Chivo" }}>{value}</span>
      <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mt-1">{label}</span>
    </div>
  );
}

export default function ValidationProfilesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [runSummary, setRunSummary] = useState(null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);

  // filters
  const [q, setQ] = useState("");
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fActive, setFActive] = useState("all");
  const [fExpected, setFExpected] = useState("all");
  const [fActual, setFActual] = useState("all");

  // form sheet
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listValidationProfiles();
      setRows(data || []);
      const stamps = (data || [])
        .map((p) => p.latest_result && p.latest_result.validated_at)
        .filter(Boolean)
        .sort();
      setLastRun(stamps.length ? stamps[stamps.length - 1] : null);
    } catch {
      toast.error("Failed to load validation profiles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    const s = { total: rows.length, pass: 0, warning: 0, fail: 0, not_run: 0, greenfield: 0, brownfield: 0, cloud_migration: 0 };
    rows.forEach((p) => {
      const st = statusOf(p);
      if (st === "PASS") s.pass++;
      else if (st === "WARNING") s.warning++;
      else if (st === "FAIL") s.fail++;
      else s.not_run++;
      const t = (p.assessment_type || "").toLowerCase();
      if (s[t] !== undefined) s[t]++;
    });
    return s;
  }, [rows]);

  // Recommendation accuracy = profiles whose PRIMARY recommendation matched
  // (PASS + WARNING), shown separately from secondary-field validation.
  const recAccuracy = summary.total ? summary.total - summary.fail : 0;
  const passPct = summary.total ? Math.round((summary.pass / summary.total) * 100) : 0;
  const health = summary.fail > 0 ? "red" : summary.warning > 0 ? "amber" : "green";
  const HEALTH_META = {
    green: { label: "Green — No failing profiles", color: "#047857", bg: "#ECFDF5", dot: "#047857" },
    amber: { label: "Amber — One or more warnings", color: "#B45309", bg: "#FFFBEB", dot: "#B45309" },
    red: { label: "Red — One or more failures", color: "#B91C1C", bg: "#FEF2F2", dot: "#B91C1C" },
  };

  // Dynamic regression classification vs the current approved benchmark.
  const classifyRun = useCallback((data) => {
    const known = [], newWarn = [], regressions = [], improvements = [];
    data.forEach((p) => {
      const st = statusOf(p);
      const isKnown = KNOWN_WARNINGS.includes(p.profile_code);
      if (st === "FAIL") regressions.push(p.profile_code);
      else if (st === "WARNING") (isKnown ? known : newWarn).push(p.profile_code);
      else if (st === "PASS" && isKnown) improvements.push(p.profile_code);
    });
    const c = { total: data.length, pass: 0, warning: 0, fail: 0 };
    data.forEach((p) => {
      const st = statusOf(p);
      if (st === "PASS") c.pass++; else if (st === "WARNING") c.warning++; else if (st === "FAIL") c.fail++;
    });
    return {
      ...c,
      accuracy: `${c.total - c.fail}/${c.total}`,
      overall: c.fail > 0 ? "FAIL" : "PASS",
      known, newWarn, regressions, improvements,
    };
  }, []);

  const expectedOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((p) => {
      const v = (p.assessment_type === "cloud_migration") ? p.expected_migration_strategy : p.expected_solution;
      if (v) set.add(v);
    });
    return Array.from(set);
  }, [rows]);

  const actualOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((p) => {
      const v = p.latest_result && p.latest_result.actual_recommendation;
      if (v) set.add(v);
    });
    return Array.from(set);
  }, [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((p) => {
      if (term && !(`${p.profile_code} ${p.profile_name}`.toLowerCase().includes(term))) return false;
      if (fType !== "all" && (p.assessment_type || "").toLowerCase() !== fType) return false;
      if (fStatus !== "all" && statusOf(p) !== fStatus) return false;
      if (fActive !== "all" && String(!!p.active) !== fActive) return false;
      const exp = (p.assessment_type === "cloud_migration") ? p.expected_migration_strategy : p.expected_solution;
      if (fExpected !== "all" && exp !== fExpected) return false;
      const act = p.latest_result && p.latest_result.actual_recommendation;
      if (fActual !== "all" && act !== fActual) return false;
      return true;
    });
  }, [rows, q, fType, fStatus, fActive, fExpected, fActual]);

  const handleRunAll = async () => {
    setRunning(true);
    try {
      await runAllValidationProfiles();
      const data = await listValidationProfiles();
      setRows(data || []);
      const stamps = (data || [])
        .map((p) => p.latest_result && p.latest_result.validated_at)
        .filter(Boolean).sort();
      setLastRun(stamps.length ? stamps[stamps.length - 1] : null);
      const cls = classifyRun(data || []);
      setRunSummary(cls);
      setRunDialogOpen(true);
      if (cls.regressions.length) toast.error(`Regression detected — ${cls.regressions.length} profile(s) FAILED`);
      else if (cls.newWarn.length) toast.warning(`${cls.newWarn.length} NEW warning(s) detected`);
      else toast.success(`Validation complete — ${cls.pass}/${cls.total} PASS, no new regressions`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed to run");
    } finally {
      setRunning(false);
    }
  };

  const handleRunOne = async (id) => {
    setRunningId(id);
    try {
      await runValidationProfile(id);
      await load();
      toast.success("Profile validated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setRunningId(null);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setSheetOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      profile_code: p.profile_code || "",
      profile_name: p.profile_name || "",
      assessment_type: p.assessment_type || "greenfield",
      description: p.description || "",
      business_context: p.business_context || "",
      active: p.active !== false,
      display_order: p.display_order || 0,
      module_data_json: JSON.stringify(p.module_data || {}, null, 2),
      expected_solution: p.expected_solution || "",
      expected_deployment: p.expected_deployment || "",
      expected_hosting: p.expected_hosting || "",
      expected_complexity: p.expected_complexity || "",
      expected_confidence: p.expected_confidence || "",
      expected_migration_strategy: p.expected_migration_strategy || "",
      expected_reason: p.expected_reason || "",
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.profile_code.trim()) return toast.error("Profile Code is required");
    if (!form.profile_name.trim()) return toast.error("Profile Name is required");
    let module_data;
    try {
      module_data = JSON.parse(form.module_data_json || "{}");
    } catch {
      return toast.error("Assessment Inputs must be valid JSON");
    }
    const payload = {
      profile_code: form.profile_code.trim(),
      profile_name: form.profile_name.trim(),
      assessment_type: form.assessment_type,
      description: form.description,
      business_context: form.business_context,
      active: form.active,
      display_order: Number(form.display_order) || 0,
      module_data,
      expected_solution: form.expected_solution,
      expected_deployment: form.expected_deployment,
      expected_hosting: form.expected_hosting,
      expected_complexity: form.expected_complexity,
      expected_confidence: form.expected_confidence,
      expected_migration_strategy: form.expected_migration_strategy,
      expected_reason: form.expected_reason,
    };
    setSaving(true);
    try {
      if (editing) await updateValidationProfile(editing.id, payload);
      else await createValidationProfile(payload);
      toast.success(editing ? "Profile updated" : "Profile created");
      setSheetOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (id) => {
    try { await duplicateValidationProfile(id); toast.success("Profile duplicated"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Duplicate failed"); }
  };

  const handleToggleActive = async (p) => {
    try {
      if (p.active) await deactivateValidationProfile(p.id);
      else await activateValidationProfile(p.id);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete validation profile "${p.profile_code}"? This cannot be undone.`)) return;
    try { await deleteValidationProfile(p.id); toast.success("Profile deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const isCm = form.assessment_type === "cloud_migration";
  const lastRunLabel = lastRun ? new Date(lastRun).toLocaleString() : "Never";

  return (
    <div data-testid="validation-profiles-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-1 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }} data-testid="vp-title">
              Recommendation Validation
            </h1>
            <span
              data-testid="vp-health"
              className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 border"
              style={{ color: HEALTH_META[health].color, background: HEALTH_META[health].bg, borderColor: HEALTH_META[health].dot + "55" }}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: HEALTH_META[health].dot }} />
              Validation Health: {HEALTH_META[health].label}
            </span>
          </div>
          <p className="text-xs text-[#6B7280] mt-1 max-w-3xl">
            Regression testing for SmartEvaluate recommendation engines. Every Validation Profile is
            processed through the current live recommendation engine and compared with its expected outcome.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={openCreate}
            data-testid="vp-new-btn"
            className="inline-flex items-center gap-2 text-xs font-semibold text-[#025979] border border-[#025979] hover:bg-[#025979]/5 rounded-sm px-3 py-2.5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> New Profile
          </button>
          <button
            onClick={handleRunAll}
            disabled={running}
            data-testid="vp-run-all-btn"
            className="inline-flex items-center gap-2 text-xs font-semibold text-white bg-[#025979] hover:bg-[#014459] disabled:opacity-60 rounded-sm px-4 py-2.5 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} />
            {running ? "Running…" : "Run Validation"}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-5 rounded-sm border border-[#E5E7EB] bg-white px-6 py-5 flex items-center gap-9 flex-wrap" data-testid="vp-summary-card">
        <SummaryStat label="Total" value={summary.total} color="#111827" testid="vp-summary-total" />
        <SummaryStat label="Pass" value={summary.pass} color="#047857" testid="vp-summary-pass" />
        <SummaryStat label="Warning" value={summary.warning} color="#B45309" testid="vp-summary-warning" />
        <SummaryStat label="Fail" value={summary.fail} color="#B91C1C" testid="vp-summary-fail" />
        <SummaryStat label="Not Run" value={summary.not_run} color="#6B7280" testid="vp-summary-notrun" />
        <div className="h-10 w-px bg-[#E5E7EB]" />
        <SummaryStat label="Pass %" value={`${passPct}%`} color="#025979" testid="vp-summary-passpct" />
        <div className="flex flex-col" data-testid="vp-summary-accuracy">
          <span className="text-2xl font-bold leading-none text-[#025979]" style={{ fontFamily: "Chivo" }}>{recAccuracy}/{summary.total}</span>
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mt-1">Rec. Accuracy</span>
        </div>
        <div className="h-10 w-px bg-[#E5E7EB]" />
        <SummaryStat label="Greenfield" value={summary.greenfield} color="#0F172A" testid="vp-summary-gf" />
        <SummaryStat label="Brownfield" value={summary.brownfield} color="#0F172A" testid="vp-summary-bf" />
        <SummaryStat label="Cloud Migration" value={summary.cloud_migration} color="#0F172A" testid="vp-summary-cm" />
        <div className="flex flex-col ml-auto">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Last Validation Run</span>
          <span className="text-xs text-[#0F172A] mt-1" data-testid="vp-last-run">{lastRunLabel}</span>
        </div>
      </div>

      {/* Approved Benchmark banner (informational only) */}
      <div className="mt-3 rounded-sm border border-[#025979]/25 bg-[#025979]/[0.04] px-4 py-2.5 flex items-center gap-6 flex-wrap" data-testid="vp-benchmark-banner">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#025979]">
          <ShieldCheck className="w-3.5 h-3.5" /> Current Approved Benchmark
        </span>
        <span className="text-xs text-[#0F172A]"><strong>{APPROVED_BENCHMARK.total}</strong> Profiles</span>
        <span className="text-xs text-[#047857]"><strong>{APPROVED_BENCHMARK.pass}</strong> Pass</span>
        <span className="text-xs text-[#B45309]"><strong>{APPROVED_BENCHMARK.warning}</strong> Warning</span>
        <span className="text-xs text-[#B91C1C]"><strong>{APPROVED_BENCHMARK.fail}</strong> Fail</span>
        <span className="text-xs text-[#0F172A]">Recommendation Accuracy <strong>{APPROVED_BENCHMARK.accuracy}</strong></span>
        <span className="text-[11px] text-[#6B7280] ml-auto">Known warnings: {KNOWN_WARNINGS.join(", ")} · informational only</span>
      </div>

      {/* Filters */}
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="vp-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search code or name…"
            className="pl-8 h-9 w-56 text-xs"
          />
        </div>
        <Select value={fType} onValueChange={setFType}>
          <SelectTrigger className="h-9 w-40 text-xs" data-testid="vp-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="greenfield">Greenfield</SelectItem>
            <SelectItem value="brownfield">Brownfield</SelectItem>
            <SelectItem value="cloud_migration">Cloud Migration</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="h-9 w-36 text-xs" data-testid="vp-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="PASS">Pass</SelectItem>
            <SelectItem value="WARNING">Warning</SelectItem>
            <SelectItem value="FAIL">Fail</SelectItem>
            <SelectItem value="NOT_RUN">Not Run</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fActive} onValueChange={setFActive}>
          <SelectTrigger className="h-9 w-32 text-xs" data-testid="vp-filter-active"><SelectValue placeholder="Active" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fExpected} onValueChange={setFExpected}>
          <SelectTrigger className="h-9 w-44 text-xs" data-testid="vp-filter-expected"><SelectValue placeholder="Expected" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Expected</SelectItem>
            {expectedOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fActual} onValueChange={setFActual}>
          <SelectTrigger className="h-9 w-44 text-xs" data-testid="vp-filter-actual"><SelectValue placeholder="Actual" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Actual</SelectItem>
            {actualOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      <div className="mt-4 border border-[#E5E7EB] rounded-sm overflow-x-auto bg-white">
        <table className="w-full text-sm min-w-[1100px]" data-testid="vp-table">
          <thead>
            <tr className="bg-[#F8FAFC] border-b border-[#E5E7EB] text-left text-[#6B7280]">
              <th className="px-3 py-3 font-medium">Code</th>
              <th className="px-3 py-3 font-medium">Profile</th>
              <th className="px-3 py-3 font-medium">Type</th>
              <th className="px-3 py-3 font-medium">Recommendation</th>
              <th className="px-3 py-3 font-medium">Deployment / Strategy</th>
              <th className="px-3 py-3 font-medium">Complexity</th>
              <th className="px-3 py-3 font-medium w-32">Status</th>
              <th className="px-3 py-3 font-medium">Last Validated</th>
              <th className="px-3 py-3 font-medium">Active</th>
              <th className="px-3 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-xs text-[#9CA3AF]">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-xs text-[#9CA3AF]" data-testid="vp-empty">
                No validation profiles match. Create one with <strong>New Profile</strong>.
              </td></tr>
            ) : filtered.map((p) => {
              const lr = p.latest_result || {};
              const expRec = p.assessment_type === "cloud_migration" ? p.expected_migration_strategy : p.expected_solution;
              const validated = lr.validated_at ? new Date(lr.validated_at).toLocaleString() : "—";
              const diffFields = new Set((lr.differences || []).map((d) => d.field));
              const hasRun = !!lr.validated_at;
              const isCmRow = p.assessment_type === "cloud_migration";
              const recField = isCmRow ? "migration_strategy" : "solution";
              const actualRecDisplay = isCmRow && lr.actual_deployment
                ? `${lr.actual_recommendation} · ${lr.actual_deployment}`
                : lr.actual_recommendation;
              return (
                <tr key={p.id} className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]" data-testid={`vp-row-${p.profile_code}`}>
                  <td className="px-3 py-3 font-mono text-[11px] text-[#025979] font-semibold">{p.profile_code}</td>
                  <td className="px-3 py-3">
                    <button onClick={() => navigate(`/admin/validation-profiles/${p.id}`)} className="text-[13px] font-medium text-[#111827] hover:text-[#025979] text-left" data-testid={`vp-name-${p.profile_code}`}>
                      {p.profile_name}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-[11px] text-[#6B7280]">{TYPE_LABELS[p.assessment_type] || p.assessment_type}</td>
                  <td className="px-3 py-3"><ExpectedActual expected={expRec} actual={actualRecDisplay} isDiff={hasRun ? diffFields.has(recField) : undefined} /></td>
                  <td className="px-3 py-3"><ExpectedActual expected={p.expected_deployment} actual={lr.actual_deployment} isDiff={hasRun ? diffFields.has("deployment") : undefined} /></td>
                  <td className="px-3 py-3"><ExpectedActual expected={p.expected_complexity} actual={lr.actual_complexity} isDiff={hasRun ? diffFields.has("complexity") : undefined} /></td>
                  <td className="px-3 py-3"><StatusBadge status={statusOf(p)} /></td>
                  <td className="px-3 py-3 text-[11px] text-[#6B7280]">{validated}</td>
                  <td className="px-3 py-3">
                    <span className={`text-[10px] font-semibold ${p.active ? "text-[#047857]" : "text-[#9CA3AF]"}`}>
                      {p.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button title="Run this profile" data-testid={`vp-run-${p.profile_code}`} onClick={() => handleRunOne(p.id)} className="p-1.5 rounded hover:bg-[#025979]/10 text-[#025979]">
                        <Play className={`w-3.5 h-3.5 ${runningId === p.id ? "animate-pulse" : ""}`} />
                      </button>
                      <button title="View detail" data-testid={`vp-view-${p.profile_code}`} onClick={() => navigate(`/admin/validation-profiles/${p.id}`)} className="p-1.5 rounded hover:bg-slate-100 text-[#6B7280]">
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <button title="Edit" data-testid={`vp-edit-${p.profile_code}`} onClick={() => openEdit(p)} className="p-1.5 rounded hover:bg-slate-100 text-[#6B7280]">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button title="Duplicate" data-testid={`vp-dup-${p.profile_code}`} onClick={() => handleDuplicate(p.id)} className="p-1.5 rounded hover:bg-slate-100 text-[#6B7280]">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button title={p.active ? "Deactivate" : "Activate"} data-testid={`vp-toggle-${p.profile_code}`} onClick={() => handleToggleActive(p)} className="p-1.5 rounded hover:bg-slate-100 text-[#6B7280]">
                        {p.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                      </button>
                      <button title="Delete" data-testid={`vp-delete-${p.profile_code}`} onClick={() => handleDelete(p)} className="p-1.5 rounded hover:bg-red-50 text-[#B91C1C]">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-[#9CA3AF] mt-3 max-w-4xl">
        <strong>PASS</strong> = primary recommendation and all configured secondary values match.
        <strong> WARNING</strong> = primary matches but a secondary value (deployment/hosting/complexity/confidence) drifted.
        <strong> FAIL</strong> = primary recommendation differs or the engine could not process the profile.
        Validation runs the current live engine — never cached results.
      </p>

      {/* Create / Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="vp-form-sheet">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit Validation Profile" : "New Validation Profile"}</SheetTitle>
            <SheetDescription className="text-xs">
              Processed through the live recommendation engine on each run. Assessment Inputs use the same
              questionnaire payload as a real assessment (module2 / module3 keys).
            </SheetDescription>
          </SheetHeader>

          <div className="mt-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide">Profile Code *</label>
                <Input data-testid="vp-form-code" value={form.profile_code} onChange={(e) => setForm({ ...form, profile_code: e.target.value })} placeholder="VP-004" className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide">Display Order</label>
                <Input data-testid="vp-form-order" type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} className="mt-1 h-9 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide">Profile Name *</label>
              <Input data-testid="vp-form-name" value={form.profile_name} onChange={(e) => setForm({ ...form, profile_name: e.target.value })} className="mt-1 h-9 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide">Assessment Type</label>
              <Select value={form.assessment_type} onValueChange={(v) => setForm({ ...form, assessment_type: v })}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="vp-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="greenfield">Greenfield</SelectItem>
                  <SelectItem value="brownfield">Brownfield</SelectItem>
                  <SelectItem value="cloud_migration">Cloud Migration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide">Description</label>
              <Textarea data-testid="vp-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 text-sm" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide">Business Context</label>
              <Textarea data-testid="vp-form-context" value={form.business_context} onChange={(e) => setForm({ ...form, business_context: e.target.value })} rows={2} className="mt-1 text-sm" />
            </div>

            <div className="pt-2 border-t border-[#F1F5F9]">
              <div className="text-[11px] font-bold text-[#111827] uppercase tracking-wide mb-2">Expected Results</div>
              {isCm ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] text-[#6B7280]">Expected Migration Strategy</label>
                    <Input data-testid="vp-form-strategy" value={form.expected_migration_strategy} onChange={(e) => setForm({ ...form, expected_migration_strategy: e.target.value })} placeholder="Technical Migration" className="mt-1 h-9 text-sm" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className="text-[11px] text-[#6B7280]">Hosting</label><Input data-testid="vp-form-hosting" value={form.expected_hosting} onChange={(e) => setForm({ ...form, expected_hosting: e.target.value })} className="mt-1 h-9 text-sm" /></div>
                    <div><label className="text-[11px] text-[#6B7280]">Complexity</label><Input data-testid="vp-form-complexity" value={form.expected_complexity} onChange={(e) => setForm({ ...form, expected_complexity: e.target.value })} className="mt-1 h-9 text-sm" /></div>
                    <div><label className="text-[11px] text-[#6B7280]">Confidence</label><Input data-testid="vp-form-confidence" value={form.expected_confidence} onChange={(e) => setForm({ ...form, expected_confidence: e.target.value })} className="mt-1 h-9 text-sm" /></div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-[11px] text-[#6B7280]">Solution *</label><Input data-testid="vp-form-solution" value={form.expected_solution} onChange={(e) => setForm({ ...form, expected_solution: e.target.value })} placeholder="SAP EWM Advanced" className="mt-1 h-9 text-sm" /></div>
                    <div><label className="text-[11px] text-[#6B7280]">Deployment</label><Input data-testid="vp-form-deployment" value={form.expected_deployment} onChange={(e) => setForm({ ...form, expected_deployment: e.target.value })} placeholder="Decentralized EWM" className="mt-1 h-9 text-sm" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className="text-[11px] text-[#6B7280]">Hosting</label><Input data-testid="vp-form-hosting" value={form.expected_hosting} onChange={(e) => setForm({ ...form, expected_hosting: e.target.value })} className="mt-1 h-9 text-sm" /></div>
                    <div><label className="text-[11px] text-[#6B7280]">Complexity</label><Input data-testid="vp-form-complexity" value={form.expected_complexity} onChange={(e) => setForm({ ...form, expected_complexity: e.target.value })} placeholder="High" className="mt-1 h-9 text-sm" /></div>
                    <div><label className="text-[11px] text-[#6B7280]">Confidence</label><Input data-testid="vp-form-confidence" value={form.expected_confidence} onChange={(e) => setForm({ ...form, expected_confidence: e.target.value })} className="mt-1 h-9 text-sm" /></div>
                  </div>
                </div>
              )}
              <div className="mt-3">
                <label className="text-[11px] text-[#6B7280]">Reason for Expected Recommendation</label>
                <Textarea data-testid="vp-form-reason" value={form.expected_reason} onChange={(e) => setForm({ ...form, expected_reason: e.target.value })} rows={2} className="mt-1 text-sm" />
              </div>
            </div>

            <div className="pt-2 border-t border-[#F1F5F9]">
              <label className="text-[11px] font-bold text-[#111827] uppercase tracking-wide">Assessment Inputs (JSON)</label>
              <p className="text-[10px] text-[#9CA3AF] mb-1">Complete questionnaire payload — object with <code>module2</code> / <code>module3</code>.</p>
              <Textarea data-testid="vp-form-moduledata" value={form.module_data_json} onChange={(e) => setForm({ ...form, module_data_json: e.target.value })} rows={8} className="mt-1 text-[11px] font-mono" />
            </div>

            <label className="flex items-center gap-2 text-sm text-[#111827]">
              <input type="checkbox" data-testid="vp-form-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Active (included in Run Validation)
            </label>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setSheetOpen(false)} className="text-xs">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="vp-form-save" className="text-xs bg-[#025979] hover:bg-[#014459]">
              {saving ? "Saving…" : editing ? "Save Changes" : "Create Profile"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Run Validation summary panel */}
      <Dialog open={runDialogOpen} onOpenChange={setRunDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="vp-run-summary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {runSummary?.overall === "FAIL"
                ? <XCircle className="w-5 h-5 text-[#B91C1C]" />
                : <CheckCircle2 className="w-5 h-5 text-[#047857]" />}
              Validation Complete
            </DialogTitle>
          </DialogHeader>
          {runSummary && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3 text-center">
                <div><div className="text-2xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>{runSummary.total}</div><div className="text-[10px] uppercase text-[#6B7280] font-semibold">Run</div></div>
                <div><div className="text-2xl font-bold text-[#047857]" style={{ fontFamily: "Chivo" }}>{runSummary.pass}</div><div className="text-[10px] uppercase text-[#6B7280] font-semibold">Pass</div></div>
                <div><div className="text-2xl font-bold text-[#B45309]" style={{ fontFamily: "Chivo" }}>{runSummary.warning}</div><div className="text-[10px] uppercase text-[#6B7280] font-semibold">Warning</div></div>
                <div><div className="text-2xl font-bold text-[#B91C1C]" style={{ fontFamily: "Chivo" }}>{runSummary.fail}</div><div className="text-[10px] uppercase text-[#6B7280] font-semibold">Fail</div></div>
              </div>
              <div className="flex items-center justify-between rounded-sm bg-[#F8FAFC] border border-[#E5E7EB] px-4 py-2.5">
                <span className="text-xs text-[#6B7280]">Recommendation Accuracy</span>
                <span className="text-sm font-bold text-[#025979]" data-testid="vp-run-accuracy">{runSummary.accuracy}</span>
              </div>
              <div className="flex items-center justify-between rounded-sm px-4 py-2.5 border"
                   style={{ background: runSummary.overall === "FAIL" ? "#FEF2F2" : "#ECFDF5", borderColor: runSummary.overall === "FAIL" ? "#B91C1C55" : "#04785755" }}>
                <span className="text-xs text-[#6B7280]">Overall Status</span>
                <span className="text-sm font-bold" style={{ color: runSummary.overall === "FAIL" ? "#B91C1C" : "#047857" }} data-testid="vp-run-overall">{runSummary.overall}</span>
              </div>

              <div>
                <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Known Warnings <span className="text-[#9CA3AF]">(accepted review items)</span></div>
                <div className="flex flex-wrap gap-1.5" data-testid="vp-run-known">
                  {runSummary.known.length ? runSummary.known.map((c) => (
                    <span key={c} className="text-[10px] font-mono bg-[#FFFBEB] text-[#B45309] border border-[#B45309]/30 rounded px-1.5 py-0.5">{c}</span>
                  )) : <span className="text-xs text-[#9CA3AF]">None</span>}
                </div>
              </div>

              <div>
                <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">New Regression</div>
                <div data-testid="vp-run-regression">
                  {runSummary.newWarn.length === 0 && runSummary.regressions.length === 0 ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#047857]"><CheckCircle2 className="w-3.5 h-3.5" /> None — matches approved benchmark</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {runSummary.regressions.map((c) => (
                        <span key={c} className="text-[10px] font-mono bg-[#FEF2F2] text-[#B91C1C] border border-[#B91C1C]/30 rounded px-1.5 py-0.5">{c} · FAIL</span>
                      ))}
                      {runSummary.newWarn.map((c) => (
                        <span key={c} className="text-[10px] font-mono bg-[#FFF7ED] text-[#C2410C] border border-[#C2410C]/30 rounded px-1.5 py-0.5">{c} · NEW WARNING</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {runSummary.improvements.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Improvements <span className="text-[#9CA3AF]">(known warning now passing)</span></div>
                  <div className="flex flex-wrap gap-1.5" data-testid="vp-run-improvements">
                    {runSummary.improvements.map((c) => (
                      <span key={c} className="inline-flex items-center gap-1 text-[10px] font-mono bg-[#ECFDF5] text-[#047857] border border-[#047857]/30 rounded px-1.5 py-0.5"><TrendingUp className="w-3 h-3" />{c}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
